<template>
  <div>
    <v-row class="justify-space-around my-5">
      <v-col>
        <v-row cols="12" class="justify-center">
          <v-btn
            class="mb-2"
            size="large"
            color="primary"
            @click.prevent="printTickets()"
            :loading="ticketPrintPending"
            :disabled="printDisabled"
          >
            <v-icon start>fal fa-print</v-icon>
            Print Tickets
          </v-btn>
        </v-row>
        <v-row cols="12" class="justify-center mt-3">
          <v-btn
            variant="text"
            size="small"
            color="primary"
            @click.stop.prevent="downloadTickets()"
            :href="ticketDownloadUrl"
            :loading="ticketDownloadPending"
            :disabled="printDisabled"
          >
            <v-icon start>fal fa-download</v-icon>
            Download Tickets
          </v-btn>
        </v-row>
      </v-col>
      <v-col class="d-flex justify-center">
        <v-btn
          size="large"
          variant="text"
          color="primary"
          @click="printWindow()"
        >
          <v-icon start>fal fa-print</v-icon>
          Print Receipt
        </v-btn>
      </v-col>
    </v-row>

    <v-expand-transition>
      <v-alert
        :type="ticketDownloadSuccess ? 'info' : 'warning'"
        v-if="printErrored"
      >
        Your browser encountered an error while attempting to print your
        tickets.
        <br />
        <br />
        <span v-if="ticketDownloadSuccess">
          They were downloaded as a PDF instead.
        </span>
        <span v-else> Downloading them as a PDF instead. </span>
      </v-alert>
    </v-expand-transition>

    <template v-if="completedMultiSeatTickets.length >= 1">
      <div class="text-subtitle-1 mt-4 mb-2">Your "Multi-Seat" Tickets</div>
      <v-row dense>
        <v-col
          v-for="ticket in completedMultiSeatTickets"
          :key="`multi-seat-ticket-${ticket.ticketId}`"
          cols="12"
          sm="6"
          md="4"
        >
          <v-card variant="outlined" class="pa-3">
            {{ ticket.displayName }}
            <div v-for="(seat, index) in ticket.ticketSeats">
              <individual-ticket-card
                :seat="seat"
                :index="index + 1"
                :purchase-public-id="purchase.publicId!"
                :disabled="deliveryHeld"
              />
            </div>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script setup lang="ts">
import { TicketPurchaseDtoViewModel } from "@/viewmodels.g";
import printJS from "print-js";
import { TicketStatus, AddonStatus } from "@/models.g";
import { computed, onBeforeUnmount, ref } from "vue";
import IndividualTicketCard from "./individual-ticket-card.vue";

const props = defineProps<{ purchase: TicketPurchaseDtoViewModel }>();
const ticketUrl = computed(() => {
  return `/api/TicketPurchaseDto/ticketsDownload/${props.purchase.publicId}`;
});

const ticketDownloadUrl = computed(() => {
  return ticketUrl.value + "?download=true&uid=" + canaryCookieUid.value;
});

const ticketPrintUrl = computed(() => {
  return ticketUrl.value;
});

const ticketsDisabled = computed(() => {
  // Disallow ticket print if every ticket is in a non-complete state.
  // This includes hold, refunded, and exchanged.
  return (
    props.purchase.tickets!.every((t) => t.status != TicketStatus.Completed) &&
    props.purchase
      .addons!.filter((a) => a.isPrintable)
      .every((t) => t.status != AddonStatus.Completed)
  );
});

// The tenant can hold ticket delivery for a number of hours after purchase. The server
// blocks the download routes as well; this only keeps the buttons from looking available.
// The flag is never set for logged-in users, so the private app is unaffected.
const deliveryHeld = computed(
  () => props.purchase.isTicketDeliveryHeld === true,
);

const printDisabled = computed(
  () => ticketsDisabled.value || deliveryHeld.value,
);

const completedMultiSeatTickets = computed(() =>
  (props.purchase.tickets ?? []).filter(
    (t) =>
      t.status === TicketStatus.Completed && (t.ticketSeats?.length ?? 0) > 0,
  ),
);

function printWindow() {
  window.print();
}

const canaryCookieUid = ref(Math.floor(Math.random() * 10e14));

function regenCanaryCookieUid() {
  return (canaryCookieUid.value = Math.floor(Math.random() * 10e14));
}

function canaryCookieName(uid: number) {
  return `download-ready-${uid}`;
}

const ticketDownloadPending = ref(false);
const ticketDownloadSuccess = ref(false);

// Safety valve: if the server never sets the download-ready cookie we stop
// polling and drop the spinner rather than leaving the button spinning forever.
// The download itself is unaffected and still arrives if it is merely slow.
const DOWNLOAD_CANARY_TIMEOUT_MS = 120_000;
const DOWNLOAD_CANARY_POLL_MS = 250;

let downloadCanaryInterval: number | null = null;

function stopDownloadCanaryPolling() {
  if (downloadCanaryInterval !== null) {
    window.clearInterval(downloadCanaryInterval);
    downloadCanaryInterval = null;
  }
}

function downloadTickets() {
  // The download button carries a live href, and printTickets() falls back to here on
  // error, so guard the navigation itself rather than relying on the disabled attribute.
  if (printDisabled.value) return;

  const uid = regenCanaryCookieUid();
  printErrored.value = false;

  // Clicking again replaces the previous poll instead of stacking a second one.
  stopDownloadCanaryPolling();

  ticketDownloadPending.value = true;
  ticketDownloadSuccess.value = false;

  let elapsed = 0;
  downloadCanaryInterval = window.setInterval(() => {
    if (document.cookie.indexOf(canaryCookieName(uid) + "=1") >= 0) {
      ticketDownloadPending.value = false;
      ticketDownloadSuccess.value = true;
      deleteDownloadCanaryCookie(uid);
      stopDownloadCanaryPolling();
      return;
    }

    elapsed += DOWNLOAD_CANARY_POLL_MS;
    if (elapsed >= DOWNLOAD_CANARY_TIMEOUT_MS) {
      ticketDownloadPending.value = false;
      stopDownloadCanaryPolling();
    }
  }, DOWNLOAD_CANARY_POLL_MS);

  window.location.href = ticketDownloadUrl.value;
}

onBeforeUnmount(stopDownloadCanaryPolling);

function deleteDownloadCanaryCookie(uid: number) {
  document.cookie = `${canaryCookieName(
    uid,
  )}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
}

const ticketPrintPending = ref(false);
const printErrored = ref(false);

// print-js prints a PDF by loading it into a hidden iframe and calling
// contentWindow.print() on that frame. Only desktop Chromium does that
// reliably:
//
//  - Every iOS browser is WebKit, which will not render a PDF in an iframe. It
//    does not throw either, it silently prints the top-level document, which on
//    this page means the print-only receipt instead of the tickets.
//  - Android Chrome exposes window.chrome but has no iframe PDF viewer.
//  - Firefox intermittently prints blank pages. print-js waits a fixed 1000ms
//    for pdf.js to render, which large ticket PDFs can exceed, and it then
//    hides and removes the source iframe as soon as print() returns.
//
// Everywhere else we hand the PDF to the browser's own viewer instead.
function canPrintPdfInIframe(): boolean {
  const ua = navigator.userAgent;
  const uaData = (navigator as any).userAgentData;

  const isMobile: boolean =
    typeof uaData?.mobile === "boolean"
      ? uaData.mobile
      : /Android|iPad|iPhone|iPod|Mobile/i.test(ua);

  if (isMobile) return false;

  // iPadOS 13+ in desktop mode reports a Mac user agent; touch points
  // disambiguate it from a real Mac, which reports 0. Belt-and-braces -- desktop
  // Safari has no window.chrome either -- but it keeps the iPadOS case explicit.
  if (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) {
    return false;
  }

  return !!(window as any).chrome;
}

function printTickets() {
  if (printDisabled.value) return;

  printErrored.value = false;

  if (!canPrintPdfInIframe()) {
    // Must stay synchronous inside the click handler or iOS treats the new tab
    // as a non-user-gesture popup and blocks it.
    const opened = window.open(ticketPrintUrl.value, "_blank");

    if (!opened) {
      downloadTickets();
      printErrored.value = true;
    }

    return;
  }

  ticketPrintPending.value = true;

  printJS({
    printable: ticketPrintUrl.value,
    type: "pdf",
    onPrintDialogClose: () => {
      ticketPrintPending.value = false;
    },
    onError: () => {
      // Fall back to just a download to work around
      // https://hagadone.visualstudio.com/Ticketing/_boards/board/t/Ticketing%20Team/Backlog%20items/?workitem=604
      downloadTickets();
      printErrored.value = true;
    },
    onLoadingEnd: () => {
      ticketPrintPending.value = false;
    },
  });
}
</script>
